import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Loader2, AlertCircle, Plus, ImagePlus, Trash2, Pencil } from "lucide-react";
import { PortfolioMediaPreview } from "../../components/PortfolioMediaPreview";

const MAX_PORTFOLIO_PHOTOS = 12;

export function PortfolioEditor() {
  const [media, setMedia] = useState<any[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState({ title: "", caption: "" });
  const [confirmDel, setConfirmDel] = useState<string | null>(null); // [81] delete confirm

  async function load() { try { setMedia(await api.myMedia()); } catch { setMedia([]); } }
  useEffect(() => { load(); }, []);
  if (!media) return null;

  async function onFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const input = e.currentTarget;
    const selected = Array.from(input.files || []);
    input.value = "";
    if (selected.length === 0) return;

    const remaining = Math.max(0, MAX_PORTFOLIO_PHOTOS - (media?.length ?? 0));
    if (remaining === 0) {
      setErr(`Your portfolio already has the maximum of ${MAX_PORTFOLIO_PHOTOS} photos.`);
      return;
    }

    const files = selected.slice(0, remaining);
    const skipped = selected.length - files.length;
    const failures: string[] = [];
    let uploaded = 0;

    setErr(null);
    setBusy(true);
    setUploadProgress({ current: 0, total: files.length });
    try {
      for (const [index, file] of files.entries()) {
        setUploadProgress({ current: index + 1, total: files.length });
        try {
          await api.uploadMedia(file);
          uploaded++;
        } catch (ex: any) {
          failures.push(`${file.name}: ${ex?.message || "Could not upload image."}`);
        }
      }
      await load();

      if (failures.length > 0) {
        setErr(`${uploaded} of ${files.length} selected photos uploaded. ${failures[0]}`);
      } else if (skipped > 0) {
        setErr(`Uploaded ${uploaded} photos. ${skipped} ${skipped === 1 ? "photo was" : "photos were"} skipped because the portfolio limit is ${MAX_PORTFOLIO_PHOTOS}.`);
      }
    } finally {
      setUploadProgress(null);
      setBusy(false);
    }
  }
  async function remove(id: string) {
    setBusy(true);
    try { await api.deleteMedia(id); setConfirmDel(null); await load(); } catch { /* ignore */ } finally { setBusy(false); }
  }
  function startEdit(m: any) { setEditing(m.id); setDraft({ title: m.title || "", caption: m.caption || "" }); }
  async function saveEdit(id: string) {
    setBusy(true);
    try { await api.updateMedia(id, { title: draft.title, caption: draft.caption }); setEditing(null); await load(); }
    catch (ex: any) { setErr(ex?.message || "Could not save."); } finally { setBusy(false); }
  }

  const remaining = Math.max(0, MAX_PORTFOLIO_PHOTOS - media.length);

  return (
    <div className="worker-profile-section worker-profile-portfolio card p-5 mt-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-navy flex items-center gap-2"><ImagePlus className="w-4 h-4" />Portfolio</h3>
        <label className={`btn-outline h-9 cursor-pointer ${(busy || remaining === 0) ? "opacity-50 pointer-events-none" : ""}`}>
          {uploadProgress ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          {uploadProgress ? `Uploading ${uploadProgress.current}/${uploadProgress.total}` : remaining === 0 ? "Portfolio full" : "Add photos"}
          <input type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden" onChange={onFiles} disabled={busy || remaining === 0} />
        </label>
      </div>
      <p className="text-xs text-mut mb-3">Showcase photos of your past work. Select up to {remaining} {remaining === 1 ? "photo" : "photos"} now ({media.length}/{MAX_PORTFOLIO_PHOTOS} uploaded).</p>
      {err && <p className="text-sm text-brand-red mb-2 flex items-center gap-1"><AlertCircle className="w-4 h-4" />{err}</p>}

      {media.length === 0 ? (
        <div className="text-sm text-mut text-center py-6 border border-dashed border-line rounded-lg">No portfolio photos yet. Add your best work to stand out.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {media.map((m) => (
            <div key={m.id} className="rounded-lg overflow-hidden border border-line group relative">
              <PortfolioMediaPreview mediaId={m.id} alt={m.title || "Portfolio item"} className="w-full h-28 object-cover" />
              {editing === m.id ? (
                <div className="p-2 space-y-1.5">
                  <input className="field h-8 text-xs" placeholder="Title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
                  <input className="field h-8 text-xs" placeholder="Caption" value={draft.caption} onChange={(e) => setDraft({ ...draft, caption: e.target.value })} />
                  <div className="flex gap-1">
                    <button onClick={() => saveEdit(m.id)} disabled={busy} className="btn-red h-7 text-xs flex-1">Save</button>
                    <button onClick={() => setEditing(null)} className="btn-ghost h-7 text-xs">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="p-2">
                  {m.title && <div className="text-xs font-semibold text-navy truncate">{m.title}</div>}
                  {m.caption && <div className="text-[11px] text-mut truncate">{m.caption}</div>}
                  {confirmDel === m.id ? (
                    <div className="flex items-center gap-1 mt-1">
                      <span className="text-[11px] text-mut">Delete?</span>
                      <button onClick={() => remove(m.id)} disabled={busy} className="text-[11px] font-semibold text-brand-red">Yes</button>
                      <button onClick={() => setConfirmDel(null)} className="text-[11px] text-mut hover:text-navy">No</button>
                    </div>
                  ) : (
                    <div className="flex gap-1 mt-1">
                      <button onClick={() => startEdit(m)} className="btn-ghost h-7 w-7 p-0 text-navy" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => setConfirmDel(m.id)} disabled={busy} className="btn-ghost h-7 w-7 p-0 text-brand-red" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
